import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgSelectConfig } from '@ng-select/ng-select';
import { compiledVersion as compiledVersionData } from '../compiled-version';
import { environment } from '../environments/environment';
import { Toolbox } from './classes/toolbox';
import { User } from './classes/user.class';
import { CodaltComponent } from './codalt.component';
import { ToolboxDialogComponent } from './pages/toolbox/toolbox-dialog/toolbox-dialog.component';
import { AuthService } from './services/auth/auth.service';
import { AuthorisationService } from './services/auth/authorisation.service';
import { DialogEscapeService } from './services/dialog/dialog-escape.service';
import { EntityTypeService } from './services/entity-type.service';
import { ToolboxService } from './services/toolbox.service';
import { WebsocketService } from './services/websocket/websocket.service';
import { LocalStorage } from './storage.class';
import { Utils } from './utils.class';

interface CompiledVersionInfo {
    buildNumber?: string;
    buildId?: string;
    environment?: string;
    datetime?: string;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent extends CodaltComponent implements OnInit {
    environment = environment;
    compiledVersion: CompiledVersionInfo = compiledVersionData || {};
    tokenExpireTimeout = null;

    toolboxWarn = false;
    showToolbox = false;
    toolbox: Toolbox;
    print = false;

    parentUser: User;
    user: User;

    public constructor(
        private title: Title,
        private router: Router,
        private dialog: MatDialog,
        private authService: AuthService,
        private dialogEscapeService: DialogEscapeService,
        private toolboxService: ToolboxService,
        private entitytypeService: EntityTypeService,
        private websocketService: WebsocketService,
        private ngSelectConfig: NgSelectConfig,
        private overlayContainer: OverlayContainer
    ) {
        super();

        const container = this.overlayContainer.getContainerElement();

        container.addEventListener('contextmenu', (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;

            // Prevent only on Material backdrop
            if (target?.classList.contains('cdk-overlay-backdrop')) {
                event.preventDefault();
            }
        });

        this.ngSelectConfig.notFoundText = 'Geen items gevonden';
        this.ngSelectConfig.loadingText = 'Aan het laden...';
        this.ngSelectConfig.clearAllText = 'Alles wissen';
        this.ngSelectConfig.typeToSearchText = 'Typ om te zoeken';
        this.ngSelectConfig.fixedPlaceholder = true;
        this.dialogEscapeService.init();
    }

    ngOnInit() {
        this.entitytypeService.writeEntityTypesToDb();
        this.print = location.href.indexOf('print') !== -1;
        this.title.setTitle('Planning ' + environment.titleAppend);
        if (location.pathname === '/' && LocalStorage.getUserToken()) {
            this.router.navigateByUrl(Utils.getStartPage());
        }

        this.checkAuth();
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener(
                'storage',
                () => {
                    this.checkAuth();
                },
                false
            );
        }

        if (!!LocalStorage.getUserToken()) {
            const currentRoles = JSON.stringify(LocalStorage.getUser()?.roles);
            this.authService.getUserDetails().subscribe(user => {
                const newRoles = JSON.stringify(user.roles);
                if (currentRoles !== newRoles) {
                    console.log('Roles', currentRoles, newRoles);
                    window.location.reload();
                }
            });
        }
        this.subscriptions.add(
            this.authService.loggedIn.subscribe(loggedIn => {
                if (loggedIn) {
                    this.getToolbox();
                    this.parentUser = LocalStorage.parentUser;
                    this.user = LocalStorage.getUser();
                    this.websocketService.disconnect();
                }
            })
        );
        this.parentUser = LocalStorage.parentUser;
        this.user = LocalStorage.getUser();
    }

    backToParentUser() {
        this.router.navigateByUrl('/logout');
    }

    getToolbox() {
        if (!this.toolbox && AuthorisationService.hasFunctionality(this.Functionalities.Toolbox)) {
            this.subscriptions.add(
                this.toolboxService.getCurrentToolbox().subscribe(toolbox => {
                    this.toolbox = toolbox.data;
                    if (this.toolbox) {
                        this.toolboxWarn =
                            Utils.getTimeOrNull(this.toolbox.view_before) < Utils.getTimeOrNull(new Date());
                        this.showToolbox = !this.toolbox.read;
                    }
                })
            );
        }
    }

    viewToolbox() {
        this.dialog
            .open(ToolboxDialogComponent, {
                panelClass: 'toolbox-dialog',
                width: '99vw',
                maxWidth: '1300px',
                height: '99vh',
                minHeight: '500px',
                maxHeight: '99vh',
                disableClose: true,
                data: this.toolbox,
            })
            .afterClosed()
            .subscribe(read => {
                this.toolbox.read = read;
                this.showToolbox = !this.toolbox.read;
            });
    }

    close() {
        this.showToolbox = false;
    }

    private checkAuth() {
        if (this.tokenExpireTimeout) {
            clearTimeout(this.tokenExpireTimeout);
        }
        if (LocalStorage.getTokenDate()) {
            this.getToolbox();
            const tokenValidUntil = new Date(LocalStorage.getTokenDate());
            tokenValidUntil.setSeconds(tokenValidUntil.getSeconds() + +LocalStorage.getExpireTimeToken());
            // A timeout higher than 24 days is not allowed, limit it to 10 days
            const tenDays = 60 * 60 * 24 * 10;
            const timeToInvalid = Math.min(
                tenDays,
                Math.round((tokenValidUntil.getTime() - new Date().getTime()) / 1000)
            );
            const timeBeforeRefresh = 60 * 60 * 24; // one day
            const timeout =
                Math.max(3 + Math.random() * 30, timeToInvalid - timeBeforeRefresh - Math.round(Math.random() * 30)) *
                1000;

            this.tokenExpireTimeout = setTimeout(() => {
                console.log('Refreshing token because it times out', tokenValidUntil);
                this.authService.refresh().subscribe(
                    () => {
                        this.websocketService.disconnect();
                    },
                    () => {
                        this.authService.logout();
                    }
                );
            }, timeout);
        }
    }
}
